import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from '@/api/http';
import { tokenStore } from '@/lib/auth';
import { PublicHeader } from '@/components/public/public-header';

export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>) => ({
    next: typeof s.next === 'string' ? s.next : undefined,
    error: typeof s.error === 'string' ? s.error : undefined,
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/login' }) as { next?: string; error?: string };
  const callbackUrl = search.next ?? '/dashboard';

  const { data: providers } = useQuery({
    queryKey: ['auth-providers'],
    queryFn: () => api.get('auth/providers').json<{ google: boolean }>(),
  });
  const googleEnabled = providers?.google ?? false;

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [info, setInfo] = useState<string | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  const requestCode = useMutation({
    mutationFn: (email: string) => api.post('auth/otp/request', { json: { email } }).json<{ ok: true }>(),
    onSuccess: () => {
      setStep('code');
      setInfo('Enviamos um código de 6 dígitos. Confira seu e-mail.');
      setTimeout(() => codeRef.current?.focus(), 100);
    },
    onError: async (err: any) => {
      // SEC-09 — email não cadastrado: redireciona direto pra /register sem gastar email.
      if (err?.response?.status === 404) {
        try {
          const body = await err.response.clone().json();
          if (body?.code === 'ACCOUNT_NOT_FOUND') {
            navigate({ to: '/register', search: { email, next: search.next } });
            return;
          }
        } catch { /* fallback pro erro genérico abaixo */ }
      }
    },
  });

  const verifyCode = useMutation({
    mutationFn: (vars: { email: string; code: string }) =>
      api.post('auth/otp/verify', { json: vars }).json<{ accessToken: string; refreshToken: string }>(),
    onSuccess: (tokens) => {
      tokenStore.set(tokens.accessToken, tokens.refreshToken);
      window.location.href = callbackUrl;
    },
    onError: async (err: any) => {
      // SEC-09 — backend retorna 404 + code:ACCOUNT_NOT_FOUND quando email não tem conta.
      // Redireciona pra /register passando o email pra mostrar banner contextual.
      if (err?.response?.status === 404) {
        try {
          const body = await err.response.clone().json();
          if (body?.code === 'ACCOUNT_NOT_FOUND') {
            navigate({
              to: '/register',
              search: { email, next: search.next },
            });
            return;
          }
        } catch { /* fallback pro erro genérico abaixo */ }
      }
    },
  });

  useEffect(() => {
    if (code.length === 6) verifyCode.mutate({ email, code });
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  // SEC-CRIT-3 — state OAuth agora vem assinado pelo backend
  // (rota /auth/google/start). Não montar URL manualmente.
  async function startGoogle() {
    const { url } = await api.get('auth/google/start', {
      searchParams: { role: 'CLIENT', next: callbackUrl },
    }).json<{ url: string }>();
    window.location.href = url;
  }

  return (
    <div className="bg-[#FBF8F3] min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-lg border border-slate-100 overflow-hidden">
          <header className="px-7 pt-7 pb-2">
            <h1 className="text-2xl font-extrabold text-slate-800">Entrar na Nosso Ello</h1>
            <p className="text-sm text-slate-500 mt-1">
              {step === 'email'
                ? 'Receba um código no seu e-mail ou entre com Google'
                : `Código enviado para ${email}`}
            </p>
          </header>

          <div className="px-7 pb-7 pt-3 flex flex-col gap-4">
            {step === 'email' ? (
              <>
                <form
                  onSubmit={(e) => { e.preventDefault(); if (email) requestCode.mutate(email); }}
                  className="flex flex-col gap-3"
                >
                  <label className="block">
                    <span className="text-sm font-medium text-slate-700">Seu e-mail</span>
                    <input
                      type="email"
                      autoFocus
                      required
                      placeholder="voce@exemplo.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1.5 w-full bg-white border border-slate-200 rounded-xl h-11 px-3.5 focus:outline-none focus:ring-2 focus:ring-teal-300 transition"
                    />
                  </label>
                  {requestCode.isError && <p className="text-xs text-red-600">Falha ao enviar. Tente novamente.</p>}
                  <button
                    type="submit"
                    disabled={requestCode.isPending || !email}
                    className="w-full bg-teal-500 hover:bg-teal-600 text-white h-11 rounded-md font-medium transition disabled:opacity-50"
                  >
                    {requestCode.isPending ? 'Enviando...' : 'Receber código'}
                  </button>
                </form>

                <div className="relative my-1">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
                  <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-slate-500">ou</span></div>
                </div>

                {googleEnabled ? (
                  <button
                    type="button"
                    onClick={startGoogle}
                    className="w-full inline-flex items-center justify-center gap-2 border border-slate-200 hover:border-teal-400 hover:bg-teal-50 h-11 rounded-md font-medium text-slate-700 transition"
                  >
                    <GoogleIcon />
                    Continuar com Google
                  </button>
                ) : (
                  <button
                    disabled
                    title="Configure GOOGLE_CLIENT_ID e GOOGLE_CLIENT_SECRET no backend/.env"
                    className="w-full inline-flex items-center justify-center gap-2 border border-slate-200 h-11 rounded-md font-medium text-slate-400 cursor-not-allowed bg-slate-50"
                  >
                    <GoogleIcon />
                    Google indisponível (configurar em .env)
                  </button>
                )}

                {search.error === 'google_not_configured' && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    Google OAuth não está configurado. Use o login por código no e-mail.
                  </p>
                )}
              </>
            ) : (
              <form
                onSubmit={(e) => { e.preventDefault(); if (code.length === 6) verifyCode.mutate({ email, code }); }}
                className="flex flex-col gap-3"
              >
                {info && <p className="text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded-md px-3 py-2">{info}</p>}
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Código de 6 dígitos</span>
                  <input
                    ref={codeRef}
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    placeholder="000000"
                    value={code}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setCode(v);
                    }}
                    className="mt-1.5 w-full bg-white border border-slate-200 rounded-xl h-12 px-3 text-center text-2xl tracking-[0.4em] font-mono focus:outline-none focus:ring-2 focus:ring-teal-300 transition"
                  />
                </label>
                {verifyCode.isError && <p className="text-xs text-red-600">Código inválido ou expirado.</p>}
                <button
                  type="submit"
                  disabled={verifyCode.isPending || code.length !== 6}
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white h-11 rounded-md font-medium transition disabled:opacity-50"
                >
                  {verifyCode.isPending ? 'Verificando...' : 'Entrar'}
                </button>
                <div className="flex items-center justify-between text-xs">
                  <button
                    type="button"
                    onClick={() => { setStep('email'); setCode(''); setInfo(null); }}
                    className="text-slate-500 hover:text-slate-800"
                  >
                    ← Trocar e-mail
                  </button>
                  <button
                    type="button"
                    onClick={() => requestCode.mutate(email)}
                    disabled={requestCode.isPending}
                    className="text-teal-600 hover:underline"
                  >
                    Reenviar código
                  </button>
                </div>
              </form>
            )}

            <p className="text-xs text-slate-500 text-center mt-2">
              Ao continuar, você concorda com os{' '}
              <Link to="/termos" className="underline">Termos</Link> e{' '}
              <Link to="/privacidade" className="underline">Privacidade</Link>.
            </p>

            <p className="text-sm text-slate-600 text-center">
              Novo por aqui?{' '}
              <Link to="/register" className="text-coral-500 font-semibold hover:underline">
                Criar conta
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}
